import { useState, useMemo } from "react";
import PageGate from "../../../components/PageGate";
import { PERMS } from "../../../utils/perms";
import {
  Building2, Plus, Pencil, Trash2, Loader2, AlertCircle,
  CheckCircle2, X, Search, Globe, Upload,
} from "lucide-react";
import {
  useAdminGetAccreditedInstitutesQuery,
  useAdminCreateAccreditedInstituteMutation,
  useAdminUpdateAccreditedInstituteMutation,
  useAdminDeleteAccreditedInstituteMutation,
} from "../api/accreditedInstitutesApi";

function extractError(err: any): string {
  if (err?.data?.errors) {
    const first = Object.values(err.data.errors)[0];
    return Array.isArray(first) ? (first[0] as string) : String(first);
  }
  return err?.data?.message ?? err?.data?.error ?? "An error occurred";
}

type InstForm = {
  name: string;
  acronym: string;
  country: string;
  description: string;
  website: string;
  is_active: boolean;
};

const emptyForm: InstForm = {
  name: "", acronym: "", country: "Nigeria",
  description: "", website: "", is_active: true,
};

function AdminAccreditedInstitutesPageInner() {
  const { data: institutes = [], isLoading } = useAdminGetAccreditedInstitutesQuery();
  const [create, { isLoading: creating }] = useAdminCreateAccreditedInstituteMutation();
  const [update, { isLoading: updating }] = useAdminUpdateAccreditedInstituteMutation();
  const [remove, { isLoading: deleting }] = useAdminDeleteAccreditedInstituteMutation();

  const [showForm, setShowForm]       = useState(false);
  const [editTarget, setEditTarget]   = useState<any | null>(null);
  const [form, setForm]               = useState<InstForm>(emptyForm);
  const [logoFile, setLogoFile]       = useState<File | null>(null);
  const [formError, setFormError]     = useState("");
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
  const [deleteError, setDeleteError]   = useState("");
  const [search, setSearch]           = useState("");
  const [msg, setMsg]                 = useState<{ type: "success" | "error"; text: string } | null>(null);

  const visible = useMemo(() => {
    const q = search.toLowerCase();
    return (institutes as any[]).filter(
      (i: any) =>
        i.name?.toLowerCase().includes(q) ||
        i.acronym?.toLowerCase().includes(q) ||
        i.country?.toLowerCase().includes(q)
    );
  }, [institutes, search]);

  const set = (k: keyof InstForm) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((p) => ({ ...p, [k]: e.target.value }));

  const openCreate = () => {
    setEditTarget(null); setForm(emptyForm);
    setLogoFile(null); setFormError(""); setShowForm(true);
  };

  const openEdit = (inst: any) => {
    setEditTarget(inst);
    setForm({
      name: inst.name ?? "", acronym: inst.acronym ?? "",
      country: inst.country ?? "Nigeria", description: inst.description ?? "",
      website: inst.website ?? "", is_active: inst.is_active ?? true,
    });
    setLogoFile(null); setFormError(""); setShowForm(true);
  };

  const closeForm = () => { setShowForm(false); setEditTarget(null); };

  const handleSubmit = async () => {
    setFormError("");
    if (!form.name.trim()) { setFormError("Name is required"); return; }

    const fd = new FormData();
    fd.append("name", form.name);
    if (form.acronym)     fd.append("acronym", form.acronym);
    if (form.country)     fd.append("country", form.country);
    if (form.description) fd.append("description", form.description);
    if (form.website)     fd.append("website", form.website);
    fd.append("is_active", form.is_active ? "1" : "0");
    if (logoFile)         fd.append("logo", logoFile);

    try {
      if (editTarget) {
        await update({ id: editTarget.id, body: fd }).unwrap();
        setMsg({ type: "success", text: "Institute updated" });
      } else {
        await create(fd).unwrap();
        setMsg({ type: "success", text: "Institute created" });
      }
      closeForm();
    } catch (err) {
      setFormError(extractError(err));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteError("");
    try {
      await remove(deleteTarget.id).unwrap();
      setDeleteTarget(null);
      setMsg({ type: "success", text: "Institute deleted" });
    } catch (err) {
      setDeleteError(extractError(err));
    }
  };

  const inputCls = "w-full border-b-2 border-gray-100 py-2 font-bold text-gray-800 outline-none focus:border-indigo-500 bg-transparent text-sm";
  const textareaCls = "w-full border-b-2 border-gray-100 py-2 font-bold text-gray-800 outline-none focus:border-indigo-500 bg-transparent text-sm resize-none";

  const active   = (institutes as any[]).filter((i: any) => i.is_active).length;
  const inactive = (institutes as any[]).length - active;

  return (
    <div className="space-y-6 max-w-7xl mx-auto p-4 md:p-6 pb-20">

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {[
          { label: "Total",    val: (institutes as any[]).length, color: "indigo"  },
          { label: "Active",   val: active,                       color: "emerald" },
          { label: "Inactive", val: inactive,                     color: "amber"   },
        ].map((s, i) => (
          <div key={i} className="bg-white border border-gray-100 p-5 rounded-3xl shadow-sm flex items-center gap-4">
            <div className={`w-11 h-11 rounded-2xl flex items-center justify-center bg-${s.color}-50 text-${s.color}-600 shrink-0`}>
              <Building2 className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-0.5">{s.label}</div>
              <div className="text-xl font-black text-gray-900">{s.val}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="bg-white border border-gray-100 rounded-3xl p-8 shadow-sm">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            <div className="w-14 h-14 rounded-2xl bg-gray-900 flex items-center justify-center text-white shadow-xl shadow-gray-200">
              <Building2 className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-2xl font-black text-gray-900 tracking-tight leading-none uppercase">Accredited Institutes</h1>
              <p className="text-gray-400 text-sm mt-1.5 font-medium">External bodies accredited to organise CPD-earning events.</p>
            </div>
          </div>
          <button
            onClick={openCreate}
            className="flex items-center gap-2 bg-gray-900 text-white font-black text-sm px-6 py-3 rounded-2xl hover:bg-gray-800 transition-colors shadow-xl shadow-gray-200"
          >
            <Plus className="w-4 h-4" /> New Institute
          </button>
        </div>
      </div>

      {/* Global msg */}
      {msg && (
        <div className={`flex items-center gap-3 px-5 py-4 rounded-2xl border text-sm font-bold ${msg.type === "success" ? "bg-emerald-50 border-emerald-100 text-emerald-700" : "bg-rose-50 border-rose-100 text-rose-700"}`}>
          {msg.type === "success" ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
          {msg.text}
          <button onClick={() => setMsg(null)} className="ml-auto"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Search */}
      <div className="bg-white border border-gray-100 rounded-2xl px-4 py-3 flex items-center gap-3 shadow-sm">
        <Search className="w-4 h-4 text-gray-400 shrink-0" />
        <input
          className="flex-1 text-sm font-semibold text-gray-700 outline-none placeholder:text-gray-300 bg-transparent"
          placeholder="Search by name, acronym or country…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search && (
          <button onClick={() => setSearch("")} className="text-gray-400 hover:text-gray-600">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-indigo-500 animate-spin" /></div>
      ) : visible.length === 0 ? (
        <div className="bg-white border border-gray-100 rounded-3xl p-12 text-center">
          <Building2 className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-400 font-semibold">{search ? "No institutes match your search." : "No accredited institutes yet."}</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-100 rounded-3xl shadow-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-50">
                <th className="text-left text-[10px] font-black text-gray-400 uppercase tracking-widest px-6 py-4">#</th>
                <th className="text-left text-[10px] font-black text-gray-400 uppercase tracking-widest px-6 py-4">Institute</th>
                <th className="text-left text-[10px] font-black text-gray-400 uppercase tracking-widest px-6 py-4 hidden md:table-cell">Country</th>
                <th className="text-left text-[10px] font-black text-gray-400 uppercase tracking-widest px-6 py-4 hidden lg:table-cell">Website</th>
                <th className="text-left text-[10px] font-black text-gray-400 uppercase tracking-widest px-6 py-4">Status</th>
                <th className="px-6 py-4"></th>
              </tr>
            </thead>
            <tbody>
              {visible.map((inst: any, idx: number) => (
                <tr key={inst.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50 transition-colors">
                  <td className="px-6 py-4 text-gray-400 font-bold text-xs">{idx + 1}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      {inst.logo_url ? (
                        <img src={inst.logo_url} alt="" className="w-8 h-8 rounded-xl object-cover shrink-0" />
                      ) : (
                        <div className="w-8 h-8 rounded-xl bg-indigo-50 flex items-center justify-center shrink-0">
                          <Building2 className="w-4 h-4 text-indigo-500" />
                        </div>
                      )}
                      <div>
                        <div className="font-black text-gray-900">{inst.name}</div>
                        {inst.acronym && (
                          <div className="text-xs font-bold text-indigo-600 font-mono">{inst.acronym}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 hidden md:table-cell text-gray-500 font-medium">{inst.country ?? "—"}</td>
                  <td className="px-6 py-4 hidden lg:table-cell">
                    {inst.website ? (
                      <a href={inst.website} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1 text-indigo-600 font-medium hover:underline text-xs">
                        <Globe className="w-3 h-3" /> Visit
                      </a>
                    ) : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-xl border ${inst.is_active ? "bg-emerald-50 text-emerald-700 border-emerald-100" : "bg-gray-50 text-gray-400 border-gray-100"}`}>
                      {inst.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2 justify-end">
                      <button
                        onClick={() => openEdit(inst)}
                        className="p-2 rounded-xl bg-gray-50 text-gray-500 hover:bg-gray-100 transition-colors"
                        title="Edit"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => { setDeleteTarget(inst); setDeleteError(""); }}
                        className="p-2 rounded-xl bg-rose-50 text-rose-500 hover:bg-rose-100 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-6 py-3 border-t border-gray-50 text-xs font-bold text-gray-400">
            Showing {visible.length} of {(institutes as any[]).length} institutes
          </div>
        </div>
      )}

      {/* Create / Edit slide-in */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex">
          <div className="flex-1 bg-black/30 backdrop-blur-sm" onClick={closeForm} />
          <div className="w-full max-w-lg bg-white h-full overflow-y-auto shadow-2xl flex flex-col">
            <div className="p-7 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
              <h2 className="font-black text-gray-900 text-lg uppercase tracking-tight">
                {editTarget ? "Edit Institute" : "New Accredited Institute"}
              </h2>
              <button onClick={closeForm} className="p-2 rounded-xl hover:bg-gray-50 transition-colors">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="p-7 space-y-5 flex-1">
              <div>
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Full Name *</label>
                <input className={inputCls} value={form.name} onChange={set("name")} placeholder="e.g. Royal Institution of Chartered Surveyors" />
              </div>
              <div>
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Acronym</label>
                <input className={inputCls} value={form.acronym} onChange={set("acronym")} placeholder="e.g. RICS" />
              </div>
              <div>
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Country</label>
                <input className={inputCls} value={form.country} onChange={set("country")} placeholder="Nigeria" />
              </div>
              <div>
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Description</label>
                <textarea className={textareaCls} rows={3} value={form.description} onChange={set("description")} placeholder="Brief description of the institute…" />
              </div>
              <div>
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Website</label>
                <input className={inputCls} value={form.website} onChange={set("website")} placeholder="https://example.org" />
              </div>

              {/* Logo upload */}
              <div>
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 block">Logo</label>
                <label className="flex items-center gap-2 border-b-2 border-gray-100 py-2 cursor-pointer hover:border-indigo-500 transition-colors">
                  <Upload className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-500">{logoFile?.name ?? "Choose image…"}</span>
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => setLogoFile(e.target.files?.[0] ?? null)} />
                </label>
              </div>

              {/* Active toggle */}
              <div className="flex items-center justify-between pt-1">
                <span className="text-sm font-bold text-gray-700">Active</span>
                <button
                  onClick={() => setForm((p) => ({ ...p, is_active: !p.is_active }))}
                  className={`w-10 h-6 rounded-full transition-colors ${form.is_active ? "bg-indigo-500" : "bg-gray-200"}`}
                >
                  <span className={`block w-4 h-4 rounded-full bg-white shadow mx-1 transition-transform ${form.is_active ? "translate-x-4" : "translate-x-0"}`} />
                </button>
              </div>

              {formError && (
                <div className="flex items-center gap-2 bg-rose-50 border border-rose-100 text-rose-700 text-sm font-medium px-4 py-3 rounded-2xl">
                  <AlertCircle className="w-4 h-4 shrink-0" /> {formError}
                </div>
              )}
            </div>

            <div className="p-7 border-t border-gray-50 sticky bottom-0 bg-white">
              <button
                onClick={handleSubmit}
                disabled={creating || updating}
                className="w-full bg-gray-900 text-white font-black text-sm py-3.5 rounded-2xl hover:bg-gray-800 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
              >
                {(creating || updating) ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                {editTarget ? "Save Changes" : "Create Institute"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setDeleteTarget(null)} />
          <div className="relative bg-white rounded-[2.5rem] p-8 max-w-sm w-full mx-4 shadow-2xl">
            <h3 className="font-black text-gray-900 text-lg mb-2">Delete Institute?</h3>
            <p className="text-sm text-gray-500 font-medium mb-5">
              "<strong>{deleteTarget.name}</strong>" will be permanently removed.
            </p>
            {deleteError && (
              <div className="mb-4 text-rose-600 text-sm font-medium bg-rose-50 border border-rose-100 px-4 py-3 rounded-2xl">
                {deleteError}
              </div>
            )}
            <div className="flex gap-3">
              <button onClick={() => setDeleteTarget(null)} className="flex-1 border border-gray-100 font-black text-sm py-3 rounded-2xl text-gray-500 hover:bg-gray-50 transition-colors">Cancel</button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 bg-rose-500 text-white font-black text-sm py-3 rounded-2xl hover:bg-rose-600 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Info footer */}
      <div className="bg-gray-900 rounded-[2.5rem] p-8 text-white">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-2xl bg-white/10 flex items-center justify-center shrink-0">
            <Building2 className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-white/40 mb-1">CPD Host Types</div>
            <p className="font-bold text-sm text-white/80">
              Events can be hosted by NIQS (national), a State Chapter, or an Accredited Institute. Only accredited institutes can host external CPD events.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Accredited institutes are their own capability.
 */
export default function AdminAccreditedInstitutesPage() {
  return (
    <PageGate perm={PERMS.event.instituteManage}>
      <AdminAccreditedInstitutesPageInner />
    </PageGate>
  );
}
